/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

"use client";

import { CosmosText, EdsIcon } from "@cosmos/components";
import { SearchInput, Tab, Tabs } from "@eds-web/react";
import { useCallback } from "react";

import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";

import type { SearchSectionProps } from "./Search.section";
import type { KeyboardEvent, MouseEvent } from "react";

const styled = css({
	width: "token(sizes.cosmos.article.sm)",
	maxWidth: "100%",
	alignItems: "stretch",
	position: "relative",
	paddingBlockStart: "eds.dimension.80",
	paddingBlockEnd: "eds.dimension.40",
	marginInline: "auto",
	smDown: {
		paddingBlockStart: "eds.dimension.56",
		paddingBlockEnd: "eds.dimension.32",
	},
});

const magnifierStyle = css({
	display: "flex",
	justifyContent: "center",
	"& svg": {
		width: "eds.dimension.64!important",
		height: "eds.dimension.64!important",
	},
});

export type SearchHeaderProps = Pick<
	SearchSectionProps,
	| "title"
	| "desc"
	| "searchFilters"
	| "searchPlaceholder"
	| "clearSearchLabel"
	| "searchValue"
	| "currentFilter"
	| "defaultFilter"
	| "noFilterPath"
	| "onFilterChange"
	| "onValueChange"
>;

export const SearchHeader = ({
	title,
	desc,
	searchFilters,
	searchPlaceholder,
	clearSearchLabel,
	searchValue,
	currentFilter,
	defaultFilter,
	noFilterPath,
	onFilterChange,
	onValueChange,
}: SearchHeaderProps): React.JSX.Element => {
	const handleKeyDown = useCallback(
		(value: string, event: KeyboardEvent<HTMLAnchorElement>): void => {
			if (event.altKey || event.ctrlKey || event.shiftKey || event.metaKey) return;
			if (!["Enter", " "].includes(event.key)) return;
			event.preventDefault();

			if (!onFilterChange) return;
			onFilterChange(value, event);
		},
		[onFilterChange],
	);

	const handleClick = useCallback(
		(value: string, event: MouseEvent<HTMLAnchorElement>): void => {
			if (event.altKey || event.ctrlKey || event.shiftKey || event.metaKey) return;
			event.preventDefault();

			if (!onFilterChange) return;
			onFilterChange(value, event);
		},
		[onFilterChange],
	);

	return (
		<VStack gap="eds.dimension.32" className={cx("search-header", styled)}>
			<div className={magnifierStyle}>
				<EdsIcon icon="Magnifier" gradient="blue" size="xl" />
			</div>
			<VStack gap="eds.dimension.16">
				<CosmosText variant="heading2XL" text={title || ""} />
				<CosmosText variant="paragraphLG" text={desc || ""} />
			</VStack>
			<VStack gap="eds.dimension.24" alignItems="stretch" width="full">
				<SearchInput
					size="lg"
					label={searchPlaceholder || "Search..."}
					clearLabel={clearSearchLabel || "Clear search"}
					value={searchValue}
					onChange={onValueChange}
				></SearchInput>
				{searchFilters.length > 1 && (
					<Tabs value={currentFilter || defaultFilter || ""}>
						{searchFilters.map((searchFilter) => (
							<Tab
								as="a"
								key={searchFilter.id}
								label={searchFilter.label}
								value={searchFilter.label || ""}
								href={`${noFilterPath}&filter=${searchFilter.label}`}
								onKeyDown={(event) => {
									handleKeyDown(searchFilter.label || "", event);
								}}
								onClick={(event) => {
									handleClick(searchFilter.label || "", event);
								}}
							/>
						))}
					</Tabs>
				)}
			</VStack>
		</VStack>
	);
};
